
// eslint-disable-next-line max-classes-per-file
import {
  defineComponent, onBeforeMount,
  onMounted,
  onBeforeUnmount,
  onUpdated,
  PropType,
  Ref,
  ref,
  SetupContext,
} from 'vue';

import { useI18n } from 'vue-i18n';

import Editor from 'primevue/editor';
import EmojisButton from '@/components/fields/partials/EmojisButton.vue';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import Quill, { registerImportTemplateSpecialsRegistering } from '@/components/template-builder/utils/quill-setup';
import CustomColorPicker from '@/components/fields/partials/CustomColorPicker.vue';
import OverlayPanel from 'primevue/overlaypanel';

import { ColorFormatEnum, Translation } from '@/types';

// eslint-disable-next-line import/no-cycle
import {
  getTemplateIframeDocument,
  getTranslationLabel,
} from '@/components/template-builder/utils/helpers';

import { TemplateEditorState as state } from '@/composables/template-editor/TemplateEditor';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import { getLineHeightOptions } from '@/configs/line-height';
import { useEditorBackground } from '@/composables/useEditorBackground';
import { Variable } from '../template-builder/utils/variables-list';

export default defineComponent({
  name: 'BaseWysiwygEditor',
  components: {
    BaseInputNumber,
    OverlayPanel,
    CustomColorPicker,
    VariablesButton,
    EmojisButton,
    Editor,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    variablesList: {
      type: Array as PropType<Variable[]>,
      required: false,
      default: () => [],
    },

    translation: {
      type: Object as PropType<Translation>,
      required: false,
      default: null,
    },

  },

  emits: {
    'update:modelValue': String,
    'activate-group': String,
    'deactivate-group': String,
  },

  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();

    const overlayPanelLetterSpacingRef = ref();
    const lineHeightList = ref(getLineHeightOptions(t).unshift({
      label: t('templateBuilder.fields.defaultLineHeight'),
      value: '',
    }));
    const letterSpacingValue = ref();
    const selectedRange = ref();
    const editorKey = ref(0);
    const inputValue: Ref<string> = ref('');
    const editorRef = ref();
    const overlayPanelRef = ref();
    const overlayPanelBackgroundRef = ref();
    const colorFormat: Ref<string> = ref(ColorFormatEnum.HEX);
    const { registerEditor, unregisterEditor } = useEditorBackground();

    const cleanTrailingEmptyBlock = (html: string): string => html.replace(/<p><br><\/p>\s*$/, '');

    const valueChangeTimeout = ref<number | null>(null);

    const doValueChange = () => {
      let content = editorRef.value.quill.root.innerHTML;
      content = cleanTrailingEmptyBlock(content);
      inputValue.value = content;
      emit('update:modelValue', content);
    };

    const handleValueChange = () => {
      if (valueChangeTimeout.value) {
        window.clearTimeout(valueChangeTimeout.value);
      }
      valueChangeTimeout.value = window.setTimeout(() => {
        doValueChange();
        valueChangeTimeout.value = null;
      }, 150);
    };

    const insertContent = (value: string | number | undefined) => {
      if (editorRef.value && editorRef.value.quill) {
        editorRef.value.quill.insertText(editorRef.value.quill.selection.savedRange.index, value);
        handleValueChange();
      }
    };

    const toggleColorPicker = (event: MouseEvent) => {
      overlayPanelRef.value.toggle(event);
    };

    const toggleColorPickerBackground = (event: MouseEvent) => {
      overlayPanelBackgroundRef.value.toggle(event);
    };

    const changeColor = (color: string) => {
      editorRef.value.quill.format('color', color);
      handleValueChange();
    };

    const changeColorBackground = (color: string) => {
      editorRef.value.quill.format('background-color', color);
      handleValueChange();
    };

    const saveRangeSelection = (event: MouseEvent) => {
      // Store selected range (because when opening the panel, the range disappears)
      const range = editorRef.value.quill.getSelection();
      if (range !== null) {
        selectedRange.value = range;
      }
    };
    const toggleLetterSpacing = (event: MouseEvent) => {
      letterSpacingValue.value = null;
      overlayPanelLetterSpacingRef.value.toggle(event);
      editorRef.value.quill.setSelection(selectedRange.value);
    };

    const changeLetterSpacing = (value: any) => {
      if (selectedRange.value) {
        editorRef.value.quill.setSelection(selectedRange.value);
        editorRef.value.quill.formatText(selectedRange.value.index, selectedRange.value.length, {
          'letter-spacing': `${value}px`,
        });
        handleValueChange();
      }
    };

    const getSelectorFromTranslation = () => {
      const keys = props.translation.key.split('.');
      let attributeIdentifier = `data-spmtranslationid${keys.length > 1 ? `-${keys[1]}` : ''}`;
      if (props.translation.fieldType === 'link' && keys.length === 1) {
        attributeIdentifier = 'data-spmtranslationid-href';
      }
      return `spmtranslation[id="LANG_${props.translation.key}"], [${attributeIdentifier}="LANG_${props.translation.key}"]`;
    };

    onBeforeMount(async () => {
      editorKey.value += 1;

      const el = editorRef.value?.quill?.root;
      if (el) unregisterEditor(el);
    });

    const checkInterval = ref();

    onMounted(async () => {
      if (state.template?.informations.imported) {
        registerImportTemplateSpecialsRegistering();
      }
      if (editorRef.value && editorRef.value.quill) {
        editorRef.value.quill.disable();
      }

      inputValue.value = props.modelValue;

      // Avoids having an empty history point
      checkInterval.value = window.setInterval(() => {
        if (editorRef.value && editorRef.value.quill) {
          editorRef.value.quill.history.clear();
          window.clearInterval(checkInterval.value);
        }
      }, 200);

      const interval = setInterval(() => {
        const quillInstance = editorRef.value?.quill;
        if (quillInstance?.root) {
          const selector = getSelectorFromTranslation();
          const template = getTemplateIframeDocument();
          const refEl = template.querySelector(selector) as HTMLElement;
          registerEditor(editorRef.value?.quill?.root, refEl);
          const { applyBackgroundReferenceFromSelector } = useEditorBackground();

          applyBackgroundReferenceFromSelector(selector, template);
          clearInterval(interval);
        }
      }, 100);
    });

    onUpdated(() => {
      if (editorRef.value && editorRef.value.quill) {
        editorRef.value.quill.enable();
      }
    });

    const Delta = Quill.import('delta');
    const lineBreakMatcher = () => {
      const newDelta = new Delta();
      newDelta.insert({ break: '' });
      return newDelta;
    };

    // Update Quill behavior when using 'enter' key
    const handleEnter = (range: any) => {
      if (!state.template?.informations.imported) {
        return true; // laisse Quill faire son comportement par défaut: <p>
      }
      const { quill } = editorRef.value;
      // Remove selection to mimic native behavior
      if (range.length > 0) {
        quill.deleteText(range.index, range.length, Quill.sources.USER);
      }
      const nextCharacter = quill.getText(range.index + 1, 1);
      // Insert first line break
      quill.insertEmbed(range.index, 'break', true, Quill.sources.USER);
      let cursorDelta = 1;
      // At end-of-line/doc, insert a second break right after the first one
      if (nextCharacter.length === 0) {
        quill.insertEmbed(range.index + 1, 'break', true, Quill.sources.USER);
        cursorDelta = 2;
      }
      quill.setSelection(range.index + cursorDelta, Quill.sources.SILENT);
      handleValueChange();
      return false; // prevent Quill default (<p>) insertion
    };

    const quillModules = ref({
      clipboard: {
        matchVisual: false,
      },
      keyboard: {
        bindings: {
          // Enter => <br>
          enterBr: {
            key: 13,
            shiftKey: false,
            handler: handleEnter,
          },
          // Shift+Enter => <br> as well
          shiftEnterBr: {
            key: 13,
            shiftKey: true,
            handler: handleEnter,
          },
        },
      },
    });

    const focus = () => {
      if (editorRef.value && editorRef.value.quill) {
        editorRef.value.quill.blur();
        editorRef.value.quill.focus();
      }
    };

    const handleQuillEditorBlur = () => {
      if (props.translation) {
        emit('deactivate-group', props.translation.groupKey);
      }
    };

    const handleQuillEditorFocus = () => {
      if (props.translation) {
        emit('activate-group', props.translation.groupKey);
      }
    };

    const handleEditorLoaded = () => {
      editorRef.value.quill.root.addEventListener('blur', handleQuillEditorBlur);
      editorRef.value.quill.root.addEventListener('focus', handleQuillEditorFocus);
    };

    // Nettoyage avant démontage
    onBeforeUnmount(() => {
      // Flush any pending debounced update to avoid losing the latest changes
      if (valueChangeTimeout.value) {
        window.clearTimeout(valueChangeTimeout.value);
        doValueChange();
        valueChangeTimeout.value = null;
      }
      if (editorRef.value) {
        if (editorRef.value.quill) {
          editorRef.value.quill.root.removeEventListener('blur', handleQuillEditorBlur);
          editorRef.value.quill.root.removeEventListener('focus', handleQuillEditorFocus);
          editorRef.value.quill = null;
        }
        editorRef.value = null;
      }
      if (overlayPanelRef.value) {
        overlayPanelRef.value = null;
      }
      if (overlayPanelBackgroundRef.value) {
        overlayPanelBackgroundRef.value = null;
      }
      if (overlayPanelLetterSpacingRef.value) {
        overlayPanelLetterSpacingRef.value = null;
      }
      if (checkInterval.value) {
        window.clearInterval(checkInterval.value);
      }
    });

    return {
      t,
      inputValue,
      editorRef,
      overlayPanelRef,
      overlayPanelBackgroundRef,
      handleValueChange,
      insertContent,
      toggleColorPicker,
      toggleColorPickerBackground,
      changeColor,
      changeColorBackground,
      colorFormat,
      quillModules,
      focus,
      getTranslationLabel,
      handleEditorLoaded,
      lineHeightList,
      letterSpacingValue,
      toggleLetterSpacing,
      saveRangeSelection,
      changeLetterSpacing,
      overlayPanelLetterSpacingRef,
      editorKey,
    };
  },
});
